import { useEffect, useRef, useState } from 'react'
import { CheckCircle2, Download, LoaderCircle, RotateCcw } from 'lucide-react'
import { cmsApi } from '../lib/api'
import { isGuveImage, isLocalPreview, prepareImage, previewOf, type PreparedImage } from '../lib/images'

type Props = {
  label: string
  // The link the current image came from, and the image itself.
  link: string | null
  imageUrl: string | null
  disabled?: boolean
  // A draft keeps the link, not the image: it is prepared again on opening.
  prepareOnOpen?: boolean
  // A local preview address and the prepared image, sent to the Guve on save.
  onImported: (previewUrl: string, link: string, prepared: PreparedImage) => void
  // Busy, or a link typed and not fetched yet: the editor waits to save.
  onPendingChange: (pending: boolean) => void
  onError: (message: string) => void
}

// An image by link (PRD.md, item 2): pasting fetches it through the CMS's own
// server and prepares it like a picked file, kept in the browser for the
// preview and the framing; it reaches the Guve only when the editor saves.
export function ImageLinkInput({ label, link, imageUrl, disabled, prepareOnOpen, onImported, onPendingChange, onError }: Props) {
  const [draft, setDraft] = useState(link ?? '')
  const [busy, setBusy] = useState(false)
  const [ready, setReady] = useState('')
  const [error, setError] = useState('')
  // The link last prepared here, which a photo without a saved link (a place's)
  // doesn't keep.
  const [prepared, setPrepared] = useState('')
  const saved = link ?? ''
  const typed = draft.trim()
  const pending = busy || (Boolean(typed) && typed !== saved && typed !== prepared)
  // An image saved earlier straight from another site, before the CMS copied
  // links: it can be copied now.
  const outside = !pending && !ready && Boolean(imageUrl) && !isGuveImage(imageUrl) && !isLocalPreview(imageUrl)

  useEffect(() => { setDraft(link ?? '') }, [link])
  useEffect(() => { onPendingChange(pending) }, [pending, onPendingChange])
  const opened = useRef(false)

  const fetchLink = async (value: string) => {
    const url = value.trim()
    if (!url) return
    setBusy(true)
    setError('')
    setReady('')
    try {
      const image = await prepareImage(await cmsApi.imageFromLink(url))
      onImported(await previewOf(image), url, image)
      setPrepared(url)
      setReady(`Pronta para publicar · ${image.width} × ${image.height} px`)
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : 'Não foi possível baixar a imagem desse link.'
      setError(message)
      onError(message)
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    if (opened.current) return
    opened.current = true
    if (prepareOnOpen && link && !imageUrl) void fetchLink(link)
  })

  // A result in color, as the form's other messages: red when it failed, green
  // when the image is ready.
  const tone = busy ? '' : error ? 'error' : ready || (isLocalPreview(imageUrl) && !pending) ? 'ready' : ''
  const status = busy ? <><LoaderCircle size={14} className="image-link__spin" /> Baixando e preparando a imagem…</>
    : error ? error
      : ready ? <><CheckCircle2 size={14} /> {ready}</>
        : outside ? 'Esta imagem ainda está no site de origem. Copie para o Guve para ela não sumir se o site mudar.'
          : isLocalPreview(imageUrl) && !pending ? 'Pronta para publicar: vai para o Guve quando você salvar.'
          : pending ? 'Toque em Buscar imagem para preparar a imagem antes de salvar.'
            : imageUrl ? 'Imagem no Guve.'
              : 'Cole o link da imagem: ela vai para o Guve quando você salvar.'

  return (
    <div className="image-link">
      <div className="image-link__row">
        <input
          type="url"
          aria-label={`Link: ${label}`}
          disabled={disabled || busy}
          value={draft}
          onChange={(event) => { setDraft(event.target.value); setReady(''); setError('') }}
          onPaste={(event) => {
            const pasted = event.clipboardData.getData('text').trim()
            if (!pasted) return
            event.preventDefault()
            setDraft(pasted)
            void fetchLink(pasted)
          }}
          onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); void fetchLink(draft) } }}
          placeholder="https://…"
        />
        {outside ? (
          <button type="button" className="button button--secondary" disabled={disabled} onClick={() => void fetchLink(saved || imageUrl || '')}>
            <Download size={16} /> Copiar para o Guve
          </button>
        ) : (
          <button type="button" className="button button--secondary" disabled={disabled || busy || !typed || (Boolean(imageUrl) && (typed === saved || typed === prepared))} onClick={() => void fetchLink(draft)}>
            {/* The same link again after a failure, without retyping it. */}
            {error ? <><RotateCcw size={16} /> Tentar novamente</> : <><Download size={16} /> Buscar imagem</>}
          </button>
        )}
      </div>
      <small className={`image-link__status ${tone ? `image-link__status--${tone}` : ''}`} role="status">{status}</small>
    </div>
  )
}
